/* Partner Management → Dropshipping: switch it on per partner, view / edit each partner's dropship clients and prices. */
import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Plus, Store } from "lucide-react";
import { toast } from "sonner";
import { API, getAuthHeader } from "@/App";
import { Switch } from "@/components/ui/switch";
import { PriceListEditor } from "@/portal/dropship/PortalDropship";

const h = () => ({ headers: getAuthHeader() });
const err = (e) => e?.response?.data?.detail || "Something went wrong";

export const DropshipAdminTab = () => {
  const [d, setD] = useState(null);
  const [pid, setPid] = useState("");
  const [cid, setCid] = useState(null);
  const [nm, setNm] = useState("");
  const load = () => axios.get(`${API}/portal-admin/dropship`, h()).then((r) => setD(r.data.partners)).catch((e) => toast.error(err(e)));
  useEffect(() => { load(); }, []);
  if (!d) return <Loader2 className="mx-auto h-5 w-5 animate-spin" />;
  const p = d.find((x) => x.id === pid);
  const c = p?.clients.find((x) => x.id === cid);
  const toggle = async (x, on) => { try { await axios.put(`${API}/portal-admin/partners/${x.id}/dropship`, { on }, h()); toast.success(`Dropshipping ${on ? "on" : "off"} for ${x.name}`); load(); } catch (e) { toast.error(err(e)); } };
  const api = p && {
    update: (id, b) => axios.put(`${API}/portal-admin/dropship/${p.id}/clients/${id}`, b, h()).then((r) => r.data),
    remove: (id) => axios.delete(`${API}/portal-admin/dropship/${p.id}/clients/${id}`, h()),
  };
  const add = async () => { try { const r = await axios.post(`${API}/portal-admin/dropship/${p.id}/clients`, { name: nm }, h()); setNm(""); setCid(r.data.id); load(); } catch (e) { toast.error(err(e)); } };
  return (
    <div className="grid gap-4 md:grid-cols-[300px_1fr]" data-testid="ds-admin">
      <div className="space-y-1.5">
        <p className="flex items-center gap-1.5 text-sm font-semibold"><Store className="h-4 w-4 text-indigo-600" />Partners — switch Dropshipping on</p>
        <div className="max-h-[60vh] space-y-1 overflow-y-auto">{d.map((x) => (
          <div key={x.id} className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 ${pid === x.id ? "border-indigo-400 bg-indigo-50" : "bg-white"}`} data-testid={`ds-admin-partner-${x.id}`}>
            <button onClick={() => { setPid(x.id); setCid(x.clients[0]?.id || null); }} className="min-w-0 flex-1 text-left">
              <b className="block truncate text-sm">{x.name}</b><span className="font-mono text-[11px] text-slate-500">{x.codes.join(", ")} · {x.clients.length} client(s)</span></button>
            <Switch checked={x.dropship} onCheckedChange={(on) => toggle(x, on)} data-testid={`ds-admin-switch-${x.id}`} />
          </div>))}</div>
      </div>
      <div className="rounded-xl border bg-white p-3">
        {!p ? <p className="py-10 text-center text-sm text-slate-400">Pick a partner to see their dropship clients and price lists.</p> : <>
          <div className="mb-3 flex flex-wrap items-center gap-1.5">
            {p.clients.map((x) => <button key={x.id} onClick={() => setCid(x.id)} className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ${cid === x.id ? "bg-indigo-600 text-white ring-indigo-600" : "ring-slate-200"}`} data-testid={`ds-admin-client-${x.id}`}>{x.name} · {x.prices.length}</button>)}
            <input value={nm} onChange={(e) => setNm(e.target.value)} placeholder="Add client for this partner" className="h-7 rounded-full border px-3 text-xs" data-testid="ds-admin-new-name" />
            <button onClick={add} disabled={!nm.trim()} className="rounded-full bg-indigo-600 p-1 text-white disabled:opacity-40" data-testid="ds-admin-new-add"><Plus className="h-4 w-4" /></button>
            {!p.dropship && <span className="text-[11px] text-amber-700">Dropshipping is OFF for this partner — they can't use these yet.</span>}
          </div>
          {c ? <PriceListEditor c={c} api={api} onSaved={load} onDeleted={() => { setCid(null); load(); }} /> : <p className="py-6 text-center text-sm text-slate-400">No dropship clients yet.</p>}
        </>}
      </div>
    </div>
  );
};
